import { Box, Stack, Typography } from '@mui/material'
import { AppV1AppUserServiceUpdate } from 'c1gen/permissions'
import { C1IconButton, palette } from 'c1ui'
import { AppUserAvatar } from 'components/core/avatar'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { RobotIcon } from 'components/core/icons/RobotIcon'
import { SystemAccountIcon } from 'components/core/icons/SystemAccountIcon'
import { HasCapability } from 'components/no-ui/capability'
import { SetAccountTypeModal } from 'components/pages/app/users/modals/SetAccountTypeModal'
import { AppUserStatus } from 'components/pages/app/users/status/AppUserStatus'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { C1AppId } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { Pencil } from 'lucide-react'
import { AppUserType } from 'pbts/c1/api/app/v1/app_user'
import { FormattedMessage, useIntl } from 'react-intl'

type AppUserDetailPageHeaderProps = {
    appUser: AppUserValue,
}

export const AppUserDetailPageHeader = ({ appUser }: AppUserDetailPageHeaderProps): React.ReactElement => {
    const { open } = useSimpleModal()
    const intl = useIntl()
    const editAccountTypeTooltip = intl.formatMessage({
        defaultMessage: 'Edit account type',
        id: 'app_user_details_edit_account_type',
    })
    const getAccountTypeText = (appUserType: AppUserType): string => {
        switch (appUserType) {
            case AppUserType.SERVICE_ACCOUNT:
                return intl.formatMessage({
                    defaultMessage: 'Service account',
                    id: 'app_user_details_account_type_service_account',
                })
            case AppUserType.USER:
                return intl.formatMessage({
                    defaultMessage: 'User account',
                    id: 'app_user_details_account_type_user_account',
                })
            case AppUserType.SYSTEM_ACCOUNT:
                return intl.formatMessage({
                    defaultMessage: 'System account',
                    id: 'app_user_details_account_type_system_account',
                })
            default:
                return 'Unknown'
        }
    }

    return <Stack direction="row" spacing={3} alignItems="center" pl={2}>
        {appUser.appUserType !== AppUserType.USER && <Box
            alignItems="center"
            display="flex"
            justifyContent="center"
            sx={{
                backgroundColor: 'primary.dark',
                borderRadius: '50%',
                width: '120px',
                height: '120px',
            }}
        >
            {appUser.appUserType === AppUserType.SERVICE_ACCOUNT && <RobotIcon fill={palette.primary.extraLight} sx={{ fontSize: '54px' }} />}
            {appUser.appUserType === AppUserType.SYSTEM_ACCOUNT && <SystemAccountIcon fill="white" sx={{ fontSize: '54px' }} />}
        </Box>
        }
        {
            appUser.appUserType === AppUserType.USER && <AppUserAvatar
                displayName={appUser.displayName}
                size="xl"
                appUserType={appUser.appUserType}
                email={appUser.email}
            />
        }
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" pb={1}>
                <Typography variant="h2" component="div">
                    {appUser.displayName}
                </Typography>
                <AppUserStatus appUser={appUser} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" component="span">
                    <FormattedMessage defaultMessage="Email" id="app_user_details_email" />
                </Typography>
                <Typography>
                    {appUser.email}
                </Typography>
            </Stack>
            {appUser.username && <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" component="span">
                    <FormattedMessage defaultMessage="Username" id="app_user_details_username" />
                </Typography>
                <Typography>
                    {appUser.username}
                </Typography>
            </Stack>}
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" component="span">
                    <FormattedMessage id="app_user_details_identity_user" defaultMessage="Account owner" />
                </Typography>
                {appUser.identityUser
                    ? <UserDisplayName user={appUser.identityUser} includeDetails={false} />
                    : <C1EmptyIndicator />
                }
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" component="span">
                    <FormattedMessage id="app_user_details_identity_account_type" defaultMessage="Account type" />
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>
                        {getAccountTypeText(appUser.appUserType)}
                    </Typography>
                    {appUser.appId !== C1AppId && <HasCapability permission={AppV1AppUserServiceUpdate}>
                        <C1IconButton
                            size="small"
                            title={editAccountTypeTooltip}
                            aria-label={intl.formatMessage({
                                defaultMessage: 'Edit account type',
                                id: 'app_user_details_edit_account_type',
                            })}
                            onClick={open(SetAccountTypeModal, { appUser })}
                        >
                            <C1Icon icon={Pencil} size="sm" />
                        </C1IconButton>
                    </HasCapability>}
                </Stack>
            </Stack>
        </Stack>

    </Stack>
}
